import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardActions,
  CardContent,
  Typography,
  Button,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { API_BASE_URL } from 'src/apiConfig';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { useTranslation } from 'react-i18next';
import { Edit, Delete } from '@mui/icons-material';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import { ImageUrl } from 'src/constants/imagesConst';

/* ADMIN STRANA ZA ROLE */
const RolesPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [newRole, setNewRole] = useState({ role_name: '', notes: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/roles.php`);
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditRole = async () => {
    try {
      if (editingRole) {
        await axios.put(`${API_BASE_URL}/api/roles.php?id=${editingRole.id}`, {
          ...newRole,
          id: editingRole.id
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/roles.php`, {
          role_name: newRole.role_name,
          notes: newRole.notes
        });
      }
      fetchRoles();
      handleCloseDialog();
    } catch (error) {
      console.error('Error adding/editing role:', error);
    }
  };

  const handleDeleteRole = async () => {
    try {
      if (roleToDelete) {
        await axios.delete(`${API_BASE_URL}/api/roles.php?id=${roleToDelete}`, {
          data: { id: roleToDelete }
        });
        fetchRoles();
        handleCloseDeleteDialog();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  };

  const handleOpenDialog = (role = null) => {
    setEditingRole(role);
    if (role) {
      setNewRole({
        role_name: role.role_name,
        notes: role.notes,
        id: role.id
      });
    } else {
      setNewRole({ role_name: '', notes: '', id: null });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setNewRole({ role_name: '', notes: '' });
  };

  const handleOpenDeleteDialog = (id) => {
    setRoleToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRoleToDelete(null);
  };

  const filteredRoles = roles.filter(
    (role) =>
      (role.role_name &&
        cyrillicToLatin(role.role_name.toLowerCase()).includes(
          searchTerm.toLowerCase()
        )) ||
      (role.notes &&
        cyrillicToLatin(role.notes.toLowerCase()).includes(
          searchTerm.toLowerCase()
        ))
  );

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box
      style={{ padding: '20px' }}
      sx={{
        padding: 3,
        backgroundColor: '#f5f5f5',
        borderRadius: '15px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        maxWidth: '1800px', // Širi boks
        height: '400px'
      }}
    >
      <AppBar
        position="static"
        sx={{ backgroundColor: '#2596be', marginBottom: '20px' }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('roles.title')}
          </Typography>
          <div style={{ position: 'relative' }}>
            <InputBase
              placeholder={t('roles.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                const normalizedSearchTerm = cyrillicToLatin(
                  e.target.value.toLowerCase()
                );
                setSearchTerm(normalizedSearchTerm);
              }}
              inputProps={{ 'aria-label': 'search' }}
              sx={{
                color: 'white',
                padding: '0 10px',
                border: '1px solid white',
                borderRadius: '4px'
              }}
            />
            <IconButton sx={{ p: 1, color: 'white' }}>
              <SearchIcon />
            </IconButton>
            <Button
              startIcon={<AddIcon />}
              sx={{
                marginLeft: '20px',
                backgroundColor: 'white',
                color: '#2596be',
                padding: '8px 16px',
                fontWeight: 'bold',
                borderRadius: '10px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
              onClick={() => handleOpenDialog()}
            >
              {t('roles.addNew')}
            </Button>
          </div>
        </Toolbar>
      </AppBar>
      <Grid container spacing={3}>
        {filteredRoles.map((role) => (
          <Grid item key={role.id}>
            <Card
              sx={{
                boxShadow: 3,
                borderRadius: '15px',
                width: '400px',
                backgroundColor: '#e3f2fd',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/kengur2/assets/kengur.png"
                  alt="Complaint illustration"
                  style={{
                    width: '100px',
                    height: '100px',
                    marginRight: '10px'
                  }}
                />

                <div>
                  <Typography
                    variant="h5"
                    component="div"
                    sx={{ color: '#1976d2', fontWeight: 'bold' }}
                  >
                    {role.role_name}
                  </Typography>
                  <Typography variant="body2" sx={{ marginTop: '10px' }}>
                    {role.notes}
                  </Typography>
                </div>
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  sx={{
                    color: '#1c75bc',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#e0f7fa',
                      borderRadius: '8px'
                    }
                  }}
                  startIcon={
                    <Edit
                      sx={{
                        color: '#1c75bc',
                        fontSize: '24px'
                      }}
                    />
                  }
                  onClick={() => handleOpenDialog(role)}
                >
                  {t('roles.edit')}
                </Button>
                <Button
                  size="small"
                  sx={{
                    color: '#e91e63',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#fde0dc',
                      borderRadius: '8px'
                    }
                  }}
                  startIcon={
                    <Delete
                      sx={{
                        color: '#e91e63',
                        fontSize: '24px'
                      }}
                    />
                  }
                  onClick={() => handleOpenDeleteDialog(role.id)}
                >
                  {t('roles.delete')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            padding: 3,
            borderRadius: '16px',
            backgroundColor: '#f5f5f5',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            height: '280px',
            width: '300px'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#2596be',
            textAlign: 'center'
          }}
        >
          {editingRole ? t('roles.editTitle') : t('roles.addTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('roles.nameLabel')}
            type="text"
            fullWidth
            value={newRole.role_name}
            onChange={(e) =>
              setNewRole({ ...newRole, role_name: e.target.value })
            }
            variant="outlined"
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              marginBottom: '16px'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#333',
              backgroundColor: '#e0e0e0',
              '&:hover': {
                backgroundColor: '#d5d5d5'
              },
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '0.875rem',
              textTransform: 'none'
            }}
          >
            {t('roles.cancel')}
          </Button>
          <Button
            onClick={handleAddOrEditRole}
            sx={{
              backgroundColor: '#2596be',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#1976d2'
              },
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: 'bold',
              fontSize: '0.875rem',
              textTransform: 'none'
            }}
          >
            {t('roles.save')}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteRole}
      />
    </Box>
  );
};

export default RolesPage;
